import * as React from 'react';
import { Component } from 'react';
import moment from 'moment';
import BPromise from 'bluebird';
import { Field, Form, Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import { DateTimePickerField } from 'Src/components/DateTimePickerField/DateTimePickerField';
import { AppUser } from 'Src/views/App/App';
import { SchoolTerm, SchoolTerms } from 'Src/models/schoolTerm';
import { getSchoolTerms, addSchoolTerms, updateSchoolTerms } from 'Src/helpers/service/admin/schoolTerms';
import { Loader } from 'Src/components/Loader/Loader';
import './Terms.scss';

interface Props {
  user: AppUser;
}

interface State {
  isLoading: boolean;
  initialTerms: SchoolTermInDateFormat[];
  termsId: string;
  isEditCustomTerms: boolean;
  isEditMode: boolean;
  isSuccessModalOpen: boolean;
  isCreateTermsModalOpen: boolean;
  errors: any;
}

export interface SchoolTermInDateFormat {
  name: string;
  start: Date | string;
  end: Date | string;
}

interface ComplexTermError {
  name?: string;
  start?: string;
  end?: string;
}

const validationSchema = Yup.object().shape({
  terms: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .required('Required')
        .test('name', 'Empty name', value => {
          const isValidStringValue = typeof value !== 'undefined' && value !== '';
          return isValidStringValue;
        }),
      start: Yup.mixed().test('start', 'Empty start', value => {
        const isValidStringValue = typeof value !== 'undefined' && value !== null && value !== '';
        return isValidStringValue;
      }),
      end: Yup.mixed().test('end', 'Empty end', value => {
        const isValidStringValue = typeof value !== 'undefined' && value !== null && value !== '';
        return isValidStringValue;
      })
    })
  )
});

export class Terms extends Component<Props, State> {
  termColors = ['#227ebd', '#2f9e5a', '#f0932b', '#7c5cff'];

  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      initialTerms: undefined,
      termsId: undefined,
      isEditCustomTerms: false,
      isEditMode: false,
      isSuccessModalOpen: false,
      isCreateTermsModalOpen: false,
      errors: undefined
    };
  }

  convertDateToISOString(date: Date | string) {
    const dateString = moment(date)
      .format('YYYY-MM-DD')
      .toString();
    return moment.utc(dateString, 'YYYY-MM-DD').toISOString();
  }

  convertTermsToDate(terms: SchoolTerm[]) {
    return terms.map(term => ({
      name: term.name,
      start: new Date(term.start),
      end: new Date(term.end)
    }));
  }

  componentDidMount() {
    const { user } = this.props;

    getSchoolTerms(user).then((terms: SchoolTerms) => {
      const termErrors = terms.terms.map(() => ({
        start: { isError: false, errorText: '' },
        end: { isError: false, errorText: '' }
      }));

      this.setState({
        isLoading: false,
        initialTerms: this.convertTermsToDate(terms.terms),
        termsId: terms.id,
        errors: { terms: termErrors }
      });
    });
  }

  onSubmit = (values, initialTerms) => {
    const { user } = this.props;
    const { termsId } = this.state;
    const isTermIdExist = typeof termsId !== 'undefined';
    const complexErrors = this.getComplexErrors(values.terms);
    const isComplexErrorsExist = complexErrors.some(error => Object.keys(error).length > 0);

    if (!isComplexErrorsExist) {
      this.setState({
        isLoading: true
      });

      const termsWithConvertedDates = values.terms.map(term => ({
        name: term.name,
        start: this.convertDateToISOString(term.start),
        end: this.convertDateToISOString(term.end)
      }));

      const data = { terms: termsWithConvertedDates };
      const promise = isTermIdExist ? updateSchoolTerms(user, termsId, data) : addSchoolTerms(user, data);

      BPromise.resolve(promise).then((terms: SchoolTerms) => {
        this.setState({
          isLoading: false,
          initialTerms: this.convertTermsToDate(terms.terms),
          termsId: terms.id,
          isEditCustomTerms: false,
          isEditMode: false,
          isSuccessModalOpen: true
        });
      });
    }
  };

  getDateValue(value: Date | string): Date | null {
    if (typeof value === 'undefined' || value === null || value === '') {
      return null;
    }

    return new Date(value);
  }

  getTermColor(index: number): string {
    return this.termColors[index % this.termColors.length];
  }

  formatDate(value: Date | string): string {
    const date = this.getDateValue(value);
    return date ? moment(date).format('D MMM YYYY') : '-';
  }

  formatShortDate(value: Date | string): string {
    const date = this.getDateValue(value);
    return date ? moment(date).format('D MMM') : '-';
  }

  getWeeks(start: Date | string, end: Date | string): number {
    const startDate = this.getDateValue(start);
    const endDate = this.getDateValue(end);

    if (!startDate || !endDate || endDate < startDate) {
      return 0;
    }

    return Math.max(1, Math.round(moment(endDate).diff(moment(startDate), 'days') / 7));
  }

  getDayGap(start: Date | string, end: Date | string): number {
    const startDate = this.getDateValue(start);
    const endDate = this.getDateValue(end);

    if (!startDate || !endDate) {
      return 0;
    }

    return Math.max(0, moment(endDate).diff(moment(startDate), 'days'));
  }

  getComplexErrors(terms: SchoolTermInDateFormat[]): ComplexTermError[] {
    return terms.map((term, index) => {
      const error: ComplexTermError = {};
      const start = this.getDateValue(term.start);
      const end = this.getDateValue(term.end);

      if (!term.name || !String(term.name).trim()) {
        error.name = 'Enter a term name';
      }

      if (!start) {
        error.start = 'Pick a start date';
      }

      if (!end) {
        error.end = 'Pick an end date';
      }

      if (start && end && end <= start) {
        error.end = 'End must be after start';
      }

      if (index > 0) {
        const previousEnd = this.getDateValue(terms[index - 1].end);
        if (start && previousEnd && start <= previousEnd) {
          error.start = 'Must start after the previous term ends';
        }
      }

      return error;
    });
  }

  icon(path: string, className?: string) {
    return (
      <svg className={className || 'termsIcon'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d={path} />
      </svg>
    );
  }

  renderRibbon(terms: SchoolTermInDateFormat[]) {
    const completeTerms = terms.filter(term => this.getDateValue(term.start) && this.getDateValue(term.end));

    if (completeTerms.length === 0) {
      return null;
    }

    const starts = completeTerms.map(term => this.getDateValue(term.start).getTime());
    const ends = completeTerms.map(term => this.getDateValue(term.end).getTime());
    const min = Math.min(...starts);
    const max = Math.max(...ends);
    const span = Math.max(1, max - min);
    const pct = (value: Date | string) => ((this.getDateValue(value).getTime() - min) / span) * 100;
    const months = [];
    let month = moment(min).startOf('month');
    const endMonth = moment(max).endOf('month');

    while (month.isSameOrBefore(endMonth)) {
      const left = Math.max(0, Math.min(100, ((month.valueOf() - min) / span) * 100));
      months.push(
        <span key={month.format('YYYY-MM')} className="termsTimeline__month" style={{ left: `${left}%` }}>
          {month.format('MMM')}
        </span>
      );
      month = month.clone().add(1, 'month');
    }

    return (
      <div className="termsTimeline">
        <div className="termsTimeline__bar">
          {completeTerms.map((term, index) => {
            const originalIndex = terms.indexOf(term);
            const left = pct(term.start);
            const width = Math.max(2, pct(term.end) - left);
            const next = completeTerms[index + 1];
            const gapLeft = pct(term.end);
            const gapWidth = next ? pct(next.start) - gapLeft : 0;
            const gapDays = next ? this.getDayGap(term.end, next.start) : 0;

            return (
              <React.Fragment key={`term_${originalIndex}`}>
                <div
                  className="termsTimeline__segment"
                  title={`${term.name}: ${this.formatDate(term.start)} - ${this.formatDate(term.end)}`}
                  style={{ left: `${left}%`, width: `${width}%`, backgroundColor: this.getTermColor(originalIndex) }}
                >
                  {width > 12 ? String(term.name || '').replace(' term', '') : ''}
                </div>
                {next && gapWidth > 4 && (
                  <div className="termsTimeline__gap" title={`Holiday - ${gapDays} days`} style={{ left: `${gapLeft}%`, width: `${gapWidth}%` }}>
                    {gapWidth > 8 ? 'Holiday' : ''}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className="termsTimeline__months">{months}</div>
      </div>
    );
  }

  renderTermCard = (term, values, length, index, replace, insert, remove, formikErrors, complexErrors) => {
    const { isEditMode } = this.state;
    const validationTerm = formikErrors.terms && formikErrors.terms[index];
    const complexTerm = complexErrors[index] || {};
    const color = this.getTermColor(index);
    const canDelete = length > 1;

    return (
      <div className="termsCard" style={{ borderLeftColor: color }} key={`term_${index}`}>
        <div className="termsCard__header">
          <span className="termsCard__dot" style={{ backgroundColor: color, boxShadow: `0 0 0 3px ${color}22` }} />
          <span className="termsCard__eyebrow">Term {index + 1}</span>
          <div className="termsCard__spacer" />
          {isEditMode && canDelete && (
            <button type="button" className="termsIconButton termsIconButton--danger" title="Remove term" onClick={() => remove(index)}>
              {this.icon('M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6')}
            </button>
          )}
        </div>

        {!isEditMode ? (
          <>
            <div className="termsCard__title">{term.name || 'Untitled term'}</div>
            <div className="termsCard__meta">
              <span className="termsCard__date">
                {this.icon('M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2')}
                {this.formatDate(term.start)}
              </span>
              <span className="termsCard__arrow">-&gt;</span>
              <span>{this.formatDate(term.end)}</span>
              <span className="termsCard__pill" style={{ backgroundColor: `${color}18`, color }}>
                {this.getWeeks(term.start, term.end)} weeks
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="termsEditGrid">
              <div className="termsField termsField--full">
                <label>Term name</label>
                <Field
                  type="text"
                  name={`terms.${index}.name`}
                  className={validationTerm && validationTerm.name || complexTerm.name ? 'termsInput termsInput--invalid' : 'termsInput'}
                  value={term.name}
                  placeholder="e.g. Autumn term"
                  onChange={event => replace(index, { name: event.target.value, start: term.start, end: term.end })}
                />
                {(validationTerm && validationTerm.name || complexTerm.name) && <div className="termsError">{validationTerm && validationTerm.name || complexTerm.name}</div>}
              </div>

              <div className="termsField">
                <label>Start</label>
                <DateTimePickerField
                  value={this.getDateValue(term.start)}
                  onChange={startDate => replace(index, { name: term.name, start: startDate, end: term.end })}
                  showTimeSelect={false}
                  popperPlacement="bottom-start"
                  inputClassName={validationTerm && validationTerm.start || complexTerm.start ? 'termsDateInput termsDateInput--invalid' : 'termsDateInput'}
                  wrapperClassName="termsDateWrapper"
                />
                {(validationTerm && validationTerm.start || complexTerm.start) && <div className="termsError">{validationTerm && validationTerm.start || complexTerm.start}</div>}
              </div>

              <div className="termsField">
                <label>End</label>
                <DateTimePickerField
                  value={this.getDateValue(term.end)}
                  onChange={endDate => replace(index, { name: term.name, start: term.start, end: endDate })}
                  showTimeSelect={false}
                  popperPlacement="bottom-start"
                  inputClassName={validationTerm && validationTerm.end || complexTerm.end ? 'termsDateInput termsDateInput--invalid' : 'termsDateInput'}
                  wrapperClassName="termsDateWrapper"
                />
                {(validationTerm && validationTerm.end || complexTerm.end) && <div className="termsError">{validationTerm && validationTerm.end || complexTerm.end}</div>}
              </div>
            </div>

            <button
              type="button"
              className="termsAddButton"
              onClick={() => insert(index + 1, { name: '', start: '', end: '' })}
            >
              {this.icon('M12 5v14M5 12h14')}
              Add term below
            </button>
          </>
        )}
      </div>
    );
  };

  renderTermsForm = (initialValues, isCustom: boolean) => {
    const { isEditMode } = this.state;

    return (
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={values => this.onSubmit(values, initialValues)}
      >
        {({ errors, values, resetForm, dirty }) => {
          const complexErrors = this.getComplexErrors(values.terms);
          const hasComplexErrors = complexErrors.some(error => Object.keys(error).length > 0);
          const yearLabel = values.terms.length > 0
            ? `${moment(values.terms[0].start).format('YYYY')} / ${moment(values.terms[values.terms.length - 1].end).format('YY')}`
            : 'Academic year';

          return (
            <Form>
              <div className="termsPanel">
                <div className="termsPanel__header">
                  <div className="termsPanel__title">
                    <span className="termsPanel__icon">{this.icon('M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2')}</span>
                    <div>
                      <div className="termsPanel__name">{isCustom ? 'New academic year' : `Academic year ${yearLabel}`}</div>
                      <div className="termsPanel__count">{values.terms.length} terms</div>
                    </div>
                  </div>
                </div>
                {this.renderRibbon(values.terms)}
              </div>

              <FieldArray name="terms">
                {({ replace, insert, remove }) => (
                  <div className="termsList">
                    {values.terms.map((term, index) =>
                      this.renderTermCard(term, values, values.terms.length, index, replace, insert, remove, errors, complexErrors)
                    )}
                  </div>
                )}
              </FieldArray>

              {isEditMode && (
                <div className="termsSaveBar">
                  <div className="termsSaveBar__state">
                    <span className={dirty || isCustom ? 'termsSaveBar__dot termsSaveBar__dot--dirty' : 'termsSaveBar__dot'} />
                    {dirty || isCustom ? 'Editing term dates' : 'No changes yet'}
                    {hasComplexErrors && <span className="termsSaveBar__error">Fix highlighted dates before saving</span>}
                  </div>
                  <div className="termsSaveBar__actions">
                    <button
                      type="button"
                      className="termsButton termsButton--ghost"
                      onClick={() => {
                        resetForm();
                        this.onCancelClick();
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="termsButton termsButton--primary">
                      {this.icon('M20 6 9 17l-5-5')}
                      Save term dates
                    </button>
                  </div>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    );
  };

  onEditTermsClick = () => {
    this.setState({
      isEditMode: true,
      isEditCustomTerms: false
    });
  };

  onCreateTermsClick = () => {
    this.setState({
      isCreateTermsModalOpen: true
    });
  };

  onConfirmCreateTermsClick = () => {
    this.setState({
      isCreateTermsModalOpen: false,
      isEditMode: true,
      isEditCustomTerms: true
    });
  };

  onCancelClick = () => {
    this.setState({
      isEditMode: false,
      isEditCustomTerms: false
    });
  };

  closeSuccessModal = () => {
    this.setState({
      isSuccessModalOpen: false
    });
  };

  renderSuccessModal() {
    const { isSuccessModalOpen } = this.state;

    if (!isSuccessModalOpen) {
      return null;
    }

    return (
      <div className="termsModalOverlay">
        <div className="termsModal termsModal--small">
          <div className="termsModal__icon termsModal__icon--success">{this.icon('M20 6 9 17l-5-5')}</div>
          <div className="termsModal__title">Term dates saved</div>
          <div className="termsModal__text">Your school term dates have been updated.</div>
          <div className="termsModal__actions">
            <button type="button" className="termsButton termsButton--primary" onClick={this.closeSuccessModal}>
              OK
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderCreateTermsModal() {
    const { isCreateTermsModalOpen } = this.state;

    if (!isCreateTermsModalOpen) {
      return null;
    }

    return (
      <div className="termsModalOverlay">
        <div className="termsModal">
          <button type="button" className="termsModal__close" aria-label="Close" onClick={() => this.setState({ isCreateTermsModalOpen: false })}>
            x
          </button>
          <div className="termsModal__icon termsModal__icon--danger">{this.icon('M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01')}</div>
          <div className="termsModal__title">Create new terms?</div>
          <div className="termsModal__text">
            This deletes all your existing terms. You will build a fresh set from scratch. To add or tweak a term instead, use Edit terms.
          </div>
          <div className="termsModal__actions">
            <button type="button" className="termsButton termsButton--ghost" onClick={() => this.setState({ isCreateTermsModalOpen: false })}>
              Cancel
            </button>
            <button type="button" className="termsButton termsButton--danger" onClick={this.onConfirmCreateTermsClick}>
              {this.icon('M23 4v6h-6M1 20v-6h6M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15')}
              Start fresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isLoading, initialTerms, isEditCustomTerms, isEditMode } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const initialValues = isEditCustomTerms
      ? { terms: [{ name: '', start: '', end: '' }] }
      : { terms: initialTerms || [] };

    return (
      <div className="termsPage">
        {this.renderSuccessModal()}
        {this.renderCreateTermsModal()}

        <header className="termsHeader">
          <h1>Term dates</h1>
          {!isEditMode && (
            <div className="termsHeader__actions">
              <button type="button" className="termsButton termsButton--primary" onClick={this.onEditTermsClick}>
                {this.icon('M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z')}
                Edit terms
              </button>
              <button type="button" className="termsButton termsButton--secondary" onClick={this.onCreateTermsClick}>
                {this.icon('M23 4v6h-6M1 20v-6h6M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15')}
                Create new terms
              </button>
            </div>
          )}
        </header>

        <div className="termsShell">
          {this.renderTermsForm(initialValues, isEditCustomTerms)}
        </div>
      </div>
    );
  }
}

